import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './config';

// ─────────────────────────────────────────────
// Individual section fetchers (pure data, no setState)
// ─────────────────────────────────────────────

async function fetchHeroData() {
  const docRef = doc(db, 'home', 'body1');
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;

  const data = docSnap.data();
  return {
    topOrangeBar: data.topOrangeBar || '',
    whiteHeading: data.whiteHeading || '',
    OrangeHeading: data.OrangeHeading || '',
    smallHeading: data.smallHeading || '',
    imgLaptop: data.imgLaptop || '',
    mobileImageBg: data.mobileImageBg || '',
    imgAlt: data.imgAlt || '',
    imgTitle: data.imgTitle || '',
    button1: data.button1 || '',
    button2: data.button2 || '',
  };
}

async function fetchBody2Data() {
  const [offersSnap, bigImageSnap, mainHeadingSnap, subHeadingSnap, buttonSnap, cardsSnap] =
    await Promise.all([
      getDocs(collection(db, 'home', 'offerBar', 'offer1')),
      getDoc(doc(db, 'home', 'body2', 'bigImage', 'image')),
      getDoc(doc(db, 'home', 'body2', 'mainHeading', 'mainHeading')),
      getDoc(doc(db, 'home', 'body2', 'mainHeading', 'subHeading')),
      getDoc(doc(db, 'home', 'body2', 'button', 'button')),
      getDocs(collection(db, 'home', 'body2', 'cards')),
    ]);

  // Process offers
  const offers = [];
  offersSnap.forEach((d) => offers.push({ id: d.id, ...d.data() }));
  offers.sort((a, b) => a.id.localeCompare(b.id));

  // Process cards
  const cards = [];
  cardsSnap.forEach((d) => cards.push({ id: d.id, ...d.data() }));
  cards.sort((a, b) => a.id.localeCompare(b.id));

  return {
    offers,
    bigImage: bigImageSnap.exists() ? bigImageSnap.data() : null,
    heading: mainHeadingSnap.exists() ? mainHeadingSnap.data() : {},
    subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
    buttonText: buttonSnap.exists() ? (buttonSnap.data().text || 'Start Your Food Journey') : 'Start Your Food Journey',
    cards,
  };
}

async function fetchBody3Data() {
  const [headingSnap, subHeadingSnap, stepCardsSnap] = await Promise.all([
    getDoc(doc(db, 'home', 'body3', 'heading', 'heading')),
    getDoc(doc(db, 'home', 'body3', 'subHeading', 'subHeading')),
    getDocs(collection(db, 'home', 'body3', 'stepCards')),
  ]);

  const steps = [];
  const sortedDocs = stepCardsSnap.docs.sort((a, b) => {
    const aNum = parseInt(a.id.replace('stepCard', ''));
    const bNum = parseInt(b.id.replace('stepCard', ''));
    return aNum - bNum;
  });

  sortedDocs.forEach((d, index) => {
    const data = d.data();
    steps.push({
      number: index + 1,
      title: data.heading || `Step ${index + 1}`,
      description: data.content || '',
      name: data.name || data.heading || `Step ${index + 1}`,
      icon: ['🍳', '🥗', '⏰', '🛵'][index] || '📱',
      iconBg: ['bg-orange-100', 'bg-pink-100', 'bg-amber-100', 'bg-yellow-100'][index] || 'bg-gray-100',
      phoneMockupImage: data.phoneMockupImage || '',
    });
  });

  return {
    heading: headingSnap.exists() ? (headingSnap.data().text || '') : '',
    subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
    steps,
  };
}

async function fetchBody4Data() {
  // Fetch heading + subheading in parallel
  const [mainHeadingSnap, subHeadingSnap] = await Promise.all([
    getDoc(doc(db, 'home', 'body4', 'mainHeading', 'mainHeading')),
    getDoc(doc(db, 'home', 'body4', 'mainHeading', 'subHeading')),
  ]);

  // Fetch all 6 cards in parallel (was sequential before!)
  const cardPromises = [];
  for (let i = 1; i <= 6; i++) {
    cardPromises.push(
      getDoc(doc(db, 'home', 'body4', 'mainHeading', 'mainCards', `mainCard${i}`, `mainCard${i}`))
    );
  }
  const cardSnaps = await Promise.all(cardPromises);

  const iconBgs = ['bg-orange-100', 'bg-green-100', 'bg-amber-100', 'bg-blue-100', 'bg-purple-100', 'bg-red-100'];
  const iconColors = ['text-orange-500', 'text-green-500', 'text-amber-500', 'text-blue-500', 'text-purple-500', 'text-red-400'];

  const features = [];
  cardSnaps.forEach((snap, i) => {
    if (snap.exists()) {
      const data = snap.data();
      features.push({
        title: data.heading || '',
        description: data.content || '',
        img: data.img || null,
        iconBg: iconBgs[i],
        iconColor: iconColors[i],
      });
    }
  });

  return {
    mainHeading: mainHeadingSnap.exists() ? (mainHeadingSnap.data().text || '') : '',
    subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
    features,
  };
}

async function fetchBody5Data() {
  const [headingsSnap, bentosSnap] = await Promise.all([
    getDocs(collection(db, 'home', 'PopularBentos', 'Headings')),
    getDocs(collection(db, 'home', 'PopularBentos', 'cards')),
  ]);

  const bentos = [];
  bentosSnap.forEach((d) => {
    const data = d.data();
    bentos.push({
      id: d.id,
      title: data.heading || '',
      description: data.subHeading || '',
      chefName: data.chefName || '',
      image: data.img || '',
    });
  });
  bentos.sort((a, b) => a.id.localeCompare(b.id));

  let heading = '';
  let subHeading = '';
  if (!headingsSnap.empty) {
    const data = headingsSnap.docs[0].data();
    heading = data.heading || '';
    subHeading = data.subHeading || '';
  }

  return {
    heading,
    subHeading,
    bentos,
  };
}

async function fetchBody6Data() {
  const contentSnap = await getDoc(doc(db, 'home', 'kitchenStory'));
  if (!contentSnap.exists()) return null;

  const data = contentSnap.data();
  return {
    tag: data.upperShortLine || '',
    heading: data.heading || '',
    content: data.content || '',
    buttonText: data.instaButton || '',
    buttonLink: 'https://instagram.com/getbentobox.in',
    image: data.img || '',
    badge: 'Fresh Daily',
    badgeSubtext: 'Made with love',
  };
}

export async function fetchBody7Data() {
  const [contentSnap, testimonialsSnap] = await Promise.all([
    getDoc(doc(db, 'home', 'customerReviewsSection')),
    getDocs(collection(db, 'home', 'customerReviewsSection', 'cards')),
  ]);

  const testimonials = [];
  testimonialsSnap.forEach((d) => {
    const data = d.data();
    testimonials.push({
      id: d.id,
      name: data.name || '',
      role: data.location || '',
      rating: parseInt(data.starRating) || 5,
      quote: data.review || '',
      image: data.image || '',
    });
  });
  testimonials.sort((a, b) => a.id.localeCompare(b.id));

  let heading = '';
  let subHeading = '';
  let footerText = '';

  if (contentSnap.exists()) {
    const data = contentSnap.data();
    heading = data.heading || '';
    subHeading = data.subHeading || '';
    footerText = data.belowLine || '';
  }

  return { heading, subHeading, footerText, testimonials };
}

async function fetchBody8Data() {
  const [headingSnap, blogsSnap] = await Promise.all([
    getDoc(doc(db, 'home', 'blogsSection')),
    getDocs(collection(db, 'Blogs')),
  ]);

  const blogs = [];
  blogsSnap.forEach((d) => {
    const docId = d.id;
    if (docId.startsWith('card')) {
      const data = d.data();
      blogs.push({
        id: docId,
        heading: data.heading || '',
        author: data.author || 'Anonymous',
        authorImg: data.authorImg || '',
        date: data.date || '',
        img: data.img || '',
        alt: data.alt || '',
        title: data.title || '',
        slug: data.slug || '',
        type: data.type || 'General',
      });
    }
  });
  blogs.sort((a, b) => {
    const numA = parseInt(a.id.replace('card', '')) || 0;
    const numB = parseInt(b.id.replace('card', '')) || 0;
    return numA - numB;
  });

  return {
    heading: (headingSnap.exists() && headingSnap.data().heading) || '',
    blogs,
  };
}

export async function fetchBody9Data() {
  const contentSnap = await getDoc(doc(db, 'home', 'orangeBottomBar'));
  if (!contentSnap.exists()) return null;

  const data = contentSnap.data();
  return {
    tag: data.totalMeals || '',
    heading: data.heading1 || '',
    subHeading: data.subHeading || '',
    androidLink: data.androidLink || 'https://play.google.com/store/apps/details?id=com.home_meals_1&hl=en_IN',
    iosLink: data.iosLink || 'https://apps.apple.com/in/app/bentobox-home-cooked-meals/id6749816927',
    image: data.img || '',
  };
}

// ─────────────────────────────────────────────
// Master fetcher — single Promise.all for entire Home screen
// ─────────────────────────────────────────────

export async function fetchAllHomeData() {
  const [hero, body2, body3, body4, body5, body6, body7, body8, body9] =
    await Promise.all([
      fetchHeroData().catch((e) => { console.error('Hero fetch error:', e); return null; }),
      fetchBody2Data().catch((e) => { console.error('Body2 fetch error:', e); return null; }),
      fetchBody3Data().catch((e) => { console.error('Body3 fetch error:', e); return null; }),
      fetchBody4Data().catch((e) => { console.error('Body4 fetch error:', e); return null; }),
      fetchBody5Data().catch((e) => { console.error('Body5 fetch error:', e); return null; }),
      fetchBody6Data().catch((e) => { console.error('Body6 fetch error:', e); return null; }),
      fetchBody7Data().catch((e) => { console.error('Body7 fetch error:', e); return null; }),
      fetchBody8Data().catch((e) => { console.error('Body8 fetch error:', e); return null; }),
      fetchBody9Data().catch((e) => { console.error('Body9 fetch error:', e); return null; }),
    ]);

  return { hero, body2, body3, body4, body5, body6, body7, body8, body9 };
}
